import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Editor from "@monaco-editor/react";
import { 
  Send, Play, Terminal, CheckCircle, Mic, MicOff, User, 
  PhoneOff, LogOut, Check, MonitorSmartphone, Sun, Moon, Code, AlertTriangle, Clock
} from 'lucide-react';
import api from '../api/axios';
import Swal from 'sweetalert2';

const AI_AVATAR_URL = "https://img.freepik.com/free-psd/3d-illustration-person-with-glasses_23-2149436185.jpg";

const customStyles = `
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
`;

const Interview = () => {
  const { session_id } = useParams();
  const navigate = useNavigate();
  
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // --- BULLETPROOF THEME STATE ---
  const [isDark, setIsDark] = useState(() => {
    // 1. Check local storage first
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme) return storedTheme === 'dark';
    
    // 2. Fallback to current document class or OS preference
    return document.documentElement.classList.contains('dark') || 
           window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  const [hasStarted, setHasStarted] = useState(false); 
  const [tutorialStep, setTutorialStep] = useState(0); 
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [status, setStatus] = useState('connecting'); 
  const [isRecording, setIsRecording] = useState(false);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [micCooldown, setMicCooldown] = useState(0);
  
  const [avatarFrame, setAvatarFrame] = useState(0);

  const [tabSwitches, setTabSwitches] = useState(0);
  const MAX_SWITCHES = 3;

  const [metadata, setMetadata] = useState({ campaign_name: "Loading...", is_custom: false, interview_type: 'Hybrid', status: 'pending' });

  const [showVideo, setShowVideo] = useState(false); 
  const [isSubmitted, setIsSubmitted] = useState(false); 
  const [micLevel, setMicLevel] = useState(0);

  const [showEditor, setShowEditor] = useState(false);
  const [language, setLanguage] = useState("python");
  const [userCode, setUserCode] = useState("");
  const [problemData, setProblemData] = useState({ question: "", requirements: [] });
  const [testCases, setTestCases] = useState([]); 
  const [consoleOutput, setConsoleOutput] = useState("// ready to run");
  const [explanationDraft, setExplanationDraft] = useState(""); 
  
  const [recordingSource, setRecordingSource] = useState('chat'); 
  const recordingSourceRef = useRef(null); 
  const recordingDestRef = useRef(null);

  const ws = useRef(null);
  const messagesEndRef = useRef(null);
  const safetyTimerRef = useRef(null);
  const cooldownIntervalRef = useRef(null);
  
  const videoRef = useRef(null); 
  const videoStreamRef = useRef(null); 
  
  const screenStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);

  const testAudioCtxRef = useRef(null);
  const testMicReqRef = useRef(null);
  const playbackContextRef = useRef(null);
  const audioQueueRef = useRef([]); 
  const isSchedulingRef = useRef(false);
  const activeSourcesRef = useRef([]);

  const micContextRef = useRef(null);
  const processorRef = useRef(null);
  const streamRef = useRef(null); 
  const sourceRef = useRef(null);
  const audioChunksRef = useRef([]); 
  const nextStartTimeRef = useRef(0);

  const MIC_SAMPLE_RATE = 16000; 
  const AI_SAMPLE_RATE = 24000;  
  const BUFFER_SIZE = 4096;

  useEffect(() => {
    let interval;
    if (isAiSpeaking) {
      interval = setInterval(() => {
        setAvatarFrame(prev => (prev === 0 ? 1 : 0));
      }, 200);
    } else {
      setAvatarFrame(0);
    }
    return () => clearInterval(interval);
  }, [isAiSpeaking]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const res = await api.get(`/interview/${session_id}/metadata`);
        let fetchedData = res.data;
        let backendStatus = fetchedData.status ? String(fetchedData.status).toLowerCase().trim() : 'pending';

        if (fetchedData.created_at && backendStatus !== 'completed' && backendStatus !== 'terminated') {
            const timeString = fetchedData.created_at.endsWith('Z') ? fetchedData.created_at : `${fetchedData.created_at}Z`;
            const createdAtDate = new Date(timeString);
            const now = new Date();
            const diffInHours = Math.abs(now - createdAtDate) / (1000 * 60 * 60);
            if (diffInHours >= 24) { backendStatus = 'expired'; }
        }

        fetchedData.status = backendStatus;
        setMetadata(fetchedData);
      } catch (err) {
        console.error("Failed to fetch metadata", err);
      }
    };
    fetchMetadata();
  }, [session_id]);

  useEffect(() => {
    let isComponentMounted = true;
    const initMedia = async () => {
        if (metadata.status === 'expired' || metadata.status === 'completed' || metadata.status === 'terminated') return;

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ 
                video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" },
                audio: true 
            });

            if (!isComponentMounted) {
                stream.getTracks().forEach(t => t.stop());
                return;
            }
            
            videoStreamRef.current = stream;
            if (videoRef.current) { videoRef.current.srcObject = stream; }
            setShowVideo(true); 

            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            testAudioCtxRef.current = audioCtx;
            const analyser = audioCtx.createAnalyser();
            const source = audioCtx.createMediaStreamSource(stream);
            source.connect(analyser);
            analyser.fftSize = 256;
            const bufferLength = analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);

            const updateMicLevel = () => {
                if (!isComponentMounted) return;
                analyser.getByteFrequencyData(dataArray);
                let sum = 0;
                for(let i = 0; i < bufferLength; i++) { sum += dataArray[i]; }
                let avg = sum / bufferLength;
                setMicLevel(Math.min(100, Math.round((avg / 50) * 100)));
                testMicReqRef.current = requestAnimationFrame(updateMicLevel);
            };
            updateMicLevel();
        } catch (err) {
            console.error("Media Error:", err);
            Swal.fire({
              icon: 'warning',
              title: 'Please allow camera and microphone access to proceed.',
              confirmButtonColor: '#4F46E5',
            });
        }
    };
    
    if (metadata.status === 'pending') {
        initMedia();
    }

    return () => {
        isComponentMounted = false;
        if (testMicReqRef.current) cancelAnimationFrame(testMicReqRef.current);
        if (testAudioCtxRef.current && testAudioCtxRef.current.state !== 'closed') { testAudioCtxRef.current.close().catch(e => {}); }
        if (videoStreamRef.current) { videoStreamRef.current.getTracks().forEach(track => track.stop()); }
    };
  }, [metadata.status]);

  const handleViolationEnd = () => {
      if (ws.current?.readyState === WebSocket.OPEN) {
          ws.current.send(JSON.stringify({ type: 'control', action: 'terminate', reason: 'tab_switch_violation' }));
      }
      setIsSubmitted(true);
      setStatus('completed');
      stopMicrophone();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
      if (ws.current) ws.current.close();
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
        if (document.hidden && !isSubmitted && hasStarted && tutorialStep === 0) {
            setTabSwitches(prev => {
                const newCount = prev + 1;
                if (newCount > MAX_SWITCHES) {
                    Swal.fire({
                      icon: 'warning',
                      title: 'Interview terminated',
                      text: 'You have exceeded the maximum allowed tab switches.',
                      confirmButtonColor: '#4F46E5',
                    });
                    handleViolationEnd();
                } else {
                    Swal.fire({
                      icon: 'warning',
                      title: 'Tab switching is strictly prohibited',
                      text: `You have ${MAX_SWITCHES - newCount} warning(s) remaining before automatic disqualification.`,
                      confirmButtonColor: '#4F46E5',
                    });
                }
                return newCount;
            });
        }
    };

    const handleBeforeUnload = (e) => {
        if (!isSubmitted && hasStarted) { e.preventDefault(); e.returnValue = "Your interview is in progress. Are you sure you want to leave?"; }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isSubmitted, hasStarted, tutorialStep]);

  useEffect(() => {
      if (isSubmitted) { 
        if (videoStreamRef.current) videoStreamRef.current.getTracks().forEach(track => track.stop()); 
        if (screenStreamRef.current) screenStreamRef.current.getTracks().forEach(track => track.stop());
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
      }
  }, [isSubmitted]);

  useEffect(() => {
    if (hasStarted && videoRef.current && videoStreamRef.current) { 
        videoRef.current.srcObject = videoStreamRef.current; 
    }
  }, [hasStarted, showEditor]);

const handleStartInterview = async () => {
      if (!videoStreamRef.current || videoStreamRef.current.getVideoTracks().length === 0 || videoStreamRef.current.getAudioTracks().length === 0) {
          Swal.fire({
            icon: 'warning',
            title: 'Camera and microphone permissions are strictly required to start the interview.',
            text: 'Please allow them and refresh the page.',
            confirmButtonColor: '#4F46E5',
          });
          return;
      }

      try {
          // We only need video here now. Audio is handled by our mixer!
          const screen = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
          screenStreamRef.current = screen;

          screen.getVideoTracks()[0].onended = () => {
              if (!isSubmitted) {
                  Swal.fire({
                    icon: 'warning',
                    title: 'Screen sharing stopped',
                    text: 'The interview has been terminated.',
                    confirmButtonColor: '#4F46E5',
                  });
                  handleViolationEnd();
              }
          };

          if (testMicReqRef.current) cancelAnimationFrame(testMicReqRef.current);
          if (testAudioCtxRef.current && testAudioCtxRef.current.state !== 'closed') { testAudioCtxRef.current.close().catch(e => {}); }
          
          setHasStarted(true);
          setTutorialStep(1); 

          // 🔥 DIGITAL MIXER SETUP
          // 1. Initialize the playback context if it doesn't exist
          if (!playbackContextRef.current) {
              playbackContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
          }
          const ctx = playbackContextRef.current;
          if (ctx.state === 'suspended') await ctx.resume();

          // 2. Create the unified recording destination
          const dest = ctx.createMediaStreamDestination();
          recordingDestRef.current = dest;

          // 3. Plug the candidate's microphone into the mixer (without playing it to speakers)
          const micSource = ctx.createMediaStreamSource(videoStreamRef.current);
          micSource.connect(dest);

          // 4. Combine the screen video with our newly mixed audio track
          const combinedStream = new MediaStream([
              ...screen.getVideoTracks(),
              ...dest.stream.getAudioTracks()
          ]);

          let uploadQueue = Promise.resolve();

          // 5. Pass the combined stream to the recorder
          const mediaRecorder = new MediaRecorder(combinedStream, { mimeType: 'video/webm; codecs=vp8' });
          mediaRecorderRef.current = mediaRecorder;

          mediaRecorder.ondataavailable = (event) => {
              if (event.data.size > 0) {
                  uploadQueue = uploadQueue.then(() => {
                      return fetch(`https://recruitguru.ai/api/interview/${session_id}/upload-chunk`, {
                          method: 'POST',
                          body: event.data,
                          headers: {
                              'Content-Type': 'application/octet-stream'
                          }
                      }).catch(err => console.error("Chunk upload failed:", err));
                  });
              }
          };
      } catch (err) {
          Swal.fire({
            icon: 'warning',
            title: 'Screen sharing is mandatory for this interview.',
            text: "Please click 'Join Interview' and allow screen access.",
            confirmButtonColor: '#4F46E5',
          });
      }
  };
  const scrollToBottom = () => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); };

  useEffect(() => { if (isRecording) stopMicrophone(); }, [showEditor]);
  useEffect(() => { scrollToBottom(); }, [messages, showEditor]);
  useEffect(() => { return () => { if (cooldownIntervalRef.current) clearInterval(cooldownIntervalRef.current); }; }, []);

  const initPlaybackContext = () => {
    if (!playbackContextRef.current) playbackContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: AI_SAMPLE_RATE });
    if (playbackContextRef.current.state === 'suspended') playbackContextRef.current.resume();
  };

  const handleIncomingAudio = (base64String) => {
    initPlaybackContext();
    const binaryString = window.atob(base64String);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
    const int16 = new Int16Array(bytes.buffer);
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 32768.0;
    
    audioQueueRef.current.push(float32);
    if (!isSchedulingRef.current) processAudioQueue();
  };

  const processAudioQueue = () => {
    isSchedulingRef.current = true;
    const ctx = playbackContextRef.current;
    if (audioQueueRef.current.length === 0) {
        isSchedulingRef.current = false;
        setIsAiSpeaking(false);
        return;
    }
    setIsAiSpeaking(true);
    const float32Data = audioQueueRef.current.shift();
    const buffer = ctx.createBuffer(1, float32Data.length, 24000);
    buffer.getChannelData(0).set(float32Data);
    
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    
    // 1. Play out loud to the candidate's speakers/headphones
    source.connect(ctx.destination);
    
    // 2. 🔥 NEW: Route a copy of the AI's voice directly into the recording mixer
    if (recordingDestRef.current) {
        source.connect(recordingDestRef.current);
    }
    
    const currentTime = ctx.currentTime;
    if (nextStartTimeRef.current < currentTime) nextStartTimeRef.current = currentTime + 0.05;
    source.start(nextStartTimeRef.current);
    nextStartTimeRef.current += buffer.duration;
    
    activeSourcesRef.current.push(source);
    source.onended = () => {
        activeSourcesRef.current = activeSourcesRef.current.filter(s => s !== source);
        if (activeSourcesRef.current.length === 0 && audioQueueRef.current.length === 0) setIsAiSpeaking(false);
    };
    
    requestAnimationFrame(processAudioQueue);
  };
  const stopAudioPlayback = () => {
    audioQueueRef.current = [];
    isSchedulingRef.current = false;
    activeSourcesRef.current.forEach(source => { try { source.stop(); } catch(e) {} });
    activeSourcesRef.current = [];
    nextStartTimeRef.current = 0;
    setIsAiSpeaking(false);
    if (ws.current?.readyState === WebSocket.OPEN) ws.current.send(JSON.stringify({ type: "interrupt" }));
  };

  const convertFloat32ToInt16 = (float32) => {
    const int16 = new Int16Array(float32.length);
    for (let i = 0; i < float32.length; i++) {
      let s = Math.max(-1, Math.min(1, float32[i]));
      int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return int16.buffer;
  };

  const startCooldown = () => {
    setMicCooldown(5); 
    if (cooldownIntervalRef.current) clearInterval(cooldownIntervalRef.current);
    cooldownIntervalRef.current = setInterval(() => {
      setMicCooldown((prev) => {
        if (prev <= 1) { clearInterval(cooldownIntervalRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const triggerTranscription = () => {
    if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);

    // audioChunksRef now counts VOICED chunks only (see onaudioprocess).
    // At BUFFER_SIZE=4096 / 16kHz, each chunk = 256ms.
    // 3 chunks ≈ 750ms of actual voice — high enough to reject pure
    // silence/noise (the root cause of Whisper hallucinations) while
    // still allowing short answers like "Yes" or "No".
    const MIN_VOICED_CHUNKS = 3;

    if (audioChunksRef.current.length < MIN_VOICED_CHUNKS) {
        console.log(
            `[VAD] Ignored: only ${audioChunksRef.current.length} voiced chunk(s) ` +
            `(need ${MIN_VOICED_CHUNKS}). Audio was too quiet or empty.`
        );
        setIsProcessing(false);
        audioChunksRef.current = [];
        return;
    }

    if (ws.current?.readyState === WebSocket.OPEN) {
        if (!showEditor) setIsProcessing(true);
        ws.current.send(JSON.stringify({ type: "audio_complete" }));
        audioChunksRef.current = [];
        if (recordingSourceRef.current === 'chat') {
            safetyTimerRef.current = setTimeout(() => { setIsProcessing(false); }, 5000);
        }
    }
};

  const startMicrophone = async () => {
    try {
        if (streamRef.current) { streamRef.current.getTracks().forEach(track => track.stop()); }
        if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
        setIsProcessing(false); 
        stopAudioPlayback();
        initPlaybackContext(); 

        if (!micContextRef.current) { micContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: MIC_SAMPLE_RATE }); }
        if (micContextRef.current.state === 'suspended') await micContextRef.current.resume();

        const stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
        streamRef.current = stream; 
        const source = micContextRef.current.createMediaStreamSource(stream);
        sourceRef.current = source;
        const processor = micContextRef.current.createScriptProcessor(BUFFER_SIZE, 1, 1);
        processorRef.current = processor;

        processor.onaudioprocess = (e) => {
            if (!ws.current || ws.current.readyState !== WebSocket.OPEN) return;
            const inputData = e.inputBuffer.getChannelData(0);

            // Compute RMS for Voice Activity Detection only.
            // We do NOT modify the audio — the browser's autoGainControl in
            // getUserMedia already handles gain. The previous 500× boost was
            // amplifying the noise floor into clipped distortion, which is
            // what was causing Whisper to hallucinate "you you", "...", etc.
            let sumSquares = 0;
            for (let i = 0; i < inputData.length; i++) {
                sumSquares += inputData[i] * inputData[i];
            }
            const rms = Math.sqrt(sumSquares / inputData.length);

            // Send the raw audio untouched.
            ws.current.send(convertFloat32ToInt16(inputData));

            // VAD gate: only count chunks that actually contain voice.
            // Typical speech RMS ≈ 0.02–0.2, room noise floor < 0.005.
            // 0.01 sits comfortably above noise, below any real speech.
            if (rms > 0.01) {
                audioChunksRef.current.push(1);
            }
        };

        source.connect(processor); 
        processor.connect(micContextRef.current.destination);
        setIsRecording(true);
        if (recordingSourceRef.current === 'editor') setExplanationDraft(""); 
    } catch (err) { 
        console.error("Mic Error:", err);
        Swal.fire({
          icon: 'error',
          title: 'Mic Error',
          text: err.message,
          confirmButtonColor: '#4F46E5',
        });
        setIsRecording(false);
    }
  };

  const stopMicrophone = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (processorRef.current) processorRef.current.disconnect();
    if (sourceRef.current) sourceRef.current.disconnect();
    setIsRecording(false);
    startCooldown();
    setTimeout(() => triggerTranscription(), 200);
  };

  const toggleRecording = (source) => {
    if (micCooldown > 0) return;
    if (isRecording) {
      if (recordingSourceRef.current === source) { stopMicrophone(); }
    } else {
        recordingSourceRef.current = source;
        setRecordingSource(source);
        startMicrophone();
    }
  };

  const handleRunCode = () => {
    setConsoleOutput("Compiling & Running...");
    ws.current.send(JSON.stringify({ type: 'run_code', code: userCode, language: language }));
  };

  const handleSubmitFinal = () => {
    const payload = `Here is my answer:\nLANGUAGE: ${language}\nCODE:\n${userCode}\n\nOUTPUT:\n${consoleOutput}\n\nEXPLANATION:\n${explanationDraft}`;
    ws.current.send(JSON.stringify({ type: 'text', content: payload }));
    setMessages(prev => [...prev, { sender: 'user', text: payload, isFinal: true }]);
    setShowEditor(false); 
    setExplanationDraft("");
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    stopAudioPlayback();
    ws.current.send(JSON.stringify({ type: 'text', content: input }));
    setMessages(prev => [...prev, { sender: 'user', text: input, isFinal: true }]);
    setInput('');
  };

  const handleEndInterview = () => {
    if(window.confirm("Are you sure you want to end the interview?")) {
      if (ws.current?.readyState === WebSocket.OPEN) {
          ws.current.send(JSON.stringify({ type: 'text', content: "I would like to end the interview now." }));
      }
    }
  };

  // ==============================
  // 4. WEBSOCKET SETUP
  // ==============================
  useEffect(() => {
    if (!hasStarted || tutorialStep > 0) return;
    
    const socketUrl = `wss://recruitguru.ai/api/ws/interview/${session_id}`;
    ws.current = new WebSocket(socketUrl);
    
    ws.current.onopen = () => {
        setStatus('connected');
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'inactive') {
            mediaRecorderRef.current.start(2000);
        }
    };

    ws.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (safetyTimerRef.current) { clearTimeout(safetyTimerRef.current); safetyTimerRef.current = null; }

        if (data.type === 'audio') { handleIncomingAudio(data.data); }
        else if (data.type === 'code_problem') {
            try {
                const content = JSON.parse(data.text);
                setProblemData({ question: content.question, requirements: content.requirements || [] });
                setLanguage(content.language || "python");
                setUserCode(`# Write your ${content.language || 'python'} solution here...\n\n`);
                setTestCases(content.test_cases || []);
                setMessages(prev => [...prev, { sender: 'ai', text: content.question, isFinal: true }]);
            } catch (e) {
                setProblemData({ question: data.text, requirements: [] });
                setUserCode("# Error loading problem details.");
            }
            setShowEditor(true);
            setConsoleOutput("// ready to run");
            setExplanationDraft(""); 
        }
        else if (data.type === 'execution_result') { setConsoleOutput(data.output || "No output returned."); }
        else if (data.type === 'transcript') {
            setIsProcessing(false);
            const { sender, text, isFinal } = data;
            if ((!text || !text.trim()) && isFinal === false) return;
            if (sender === 'ai') setShowEditor(false);
            if (sender === 'user' && recordingSourceRef.current === 'editor') {
                setExplanationDraft(prev => prev + " " + text);
                return;
            }
            if (text.toLowerCase().includes("interview has ended")) {
                setIsSubmitted(true); setStatus('completed'); stopMicrophone(); ws.current.close();
            }

            setMessages(prev => {
                const lastMsg = prev[prev.length - 1];
                if (sender === 'ai' && isFinal === false) {
                    if (lastMsg && lastMsg.sender === 'ai' && lastMsg.isFinal === false) {
                        const updated = [...prev];
                        updated[updated.length - 1] = { ...lastMsg, text: lastMsg.text + text, isFinal: false };
                        return updated;
                    }
                    return [...prev, { sender: 'ai', text: text, isFinal: false }];
                }
                if (sender === 'ai' && isFinal === true) {
                    if (lastMsg && lastMsg.sender === 'ai' && lastMsg.isFinal === false) {
                        const updated = [...prev];
                        updated[updated.length - 1] = { ...lastMsg, isFinal: true };
                        return updated;
                    }
                    return [...prev, { sender: 'ai', text, isFinal: true }];
                }
                return [...prev, { sender, text, isFinal: true }];
            });
        }
        else if (data.type === 'control' && data.text?.includes("Interview Completed")) {
             setIsSubmitted(true); setStatus('completed'); stopMicrophone(); ws.current.close();
        }
      } catch (e) {}
    };

    ws.current.onclose = () => status !== 'completed' && setStatus('disconnected');
    return () => { if (ws.current) ws.current.close(); stopAudioPlayback(); };
  }, [session_id, hasStarted, tutorialStep]); 

  // ==============================
  // 5. RENDER UI
  // ==============================

  if (metadata.status === 'expired') {
    return (
      <div className="min-h-[100dvh] bg-slate-50 dark:bg-[#05050A] flex flex-col items-center justify-center p-6 text-center relative overflow-hidden font-sans transition-colors duration-500">
          <style>{customStyles}</style>
          <div className="absolute top-[-10%] left-[10%] w-[50%] h-[50%] rounded-full bg-amber-600/10 blur-[120px] animate-blob pointer-events-none"></div>
          <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-10 rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-2xl max-w-md w-full z-10 animate-in fade-in zoom-in-95">
              <div className="w-20 h-20 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 text-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Clock size={32} strokeWidth={1.5} />
              </div>
              <h1 className="text-2xl font-semibold tracking-wide text-slate-900 dark:text-white mb-3">Interview Expired</h1>
              <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed mb-8 font-light">
                  This interview link has expired. Interviews must be completed within 24 hours of receiving the invitation. Please contact your recruiter to request a new link.
              </p>
          </div>
      </div>
    );
  }

  if (metadata.status === 'completed' || metadata.status === 'terminated') {
      return (
        <div className="min-h-[100dvh] bg-slate-50 dark:bg-[#05050A] flex flex-col items-center justify-center p-6 text-center relative overflow-hidden font-sans transition-colors duration-500">
            <style>{customStyles}</style>
            <div className="absolute top-[-10%] left-[10%] w-[50%] h-[50%] rounded-full bg-slate-600/10 blur-[120px] animate-blob pointer-events-none"></div>
            <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-10 rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-2xl max-w-md w-full z-10 animate-in fade-in zoom-in-95">
                <div className="w-20 h-20 bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle size={32} strokeWidth={1.5} />
                </div>
                <h1 className="text-2xl font-semibold tracking-wide text-slate-900 dark:text-white mb-3">Interview Completed</h1>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed mb-8 font-light">
                    This interview session has already been completed and submitted for review.
                </p>
            </div>
        </div>
      );
  }

  const isCodingInterview = metadata.interview_type === 'Code Only' || metadata.interview_type === 'Hybrid';
  if (isMobile && isCodingInterview) {
    return (
      <div className="min-h-[100dvh] bg-slate-50 dark:bg-[#05050A] flex flex-col items-center justify-center p-6 text-center relative overflow-hidden font-sans transition-colors duration-500">
        <style>{customStyles}</style>
        <div className="absolute top-[-10%] left-[10%] w-[50%] h-[50%] rounded-full bg-indigo-600/10 blur-[120px] animate-blob pointer-events-none"></div>
        <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-10 rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-2xl max-w-md w-full z-10 animate-in fade-in zoom-in-95">
          <div className="w-20 h-20 bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20 text-rose-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <MonitorSmartphone size={32} strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl font-semibold tracking-wide text-slate-900 dark:text-white mb-3">Larger Screen Required</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed mb-8 font-light">
            This interview includes coding exercises that require a keyboard and a larger display. Please join from a <strong className="text-slate-900 dark:text-white">Laptop or Desktop Computer</strong> to continue.
          </p>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
      return (
        <div className="min-h-[100dvh] bg-slate-50 dark:bg-[#05050A] flex flex-col items-center justify-center p-6 text-center relative overflow-hidden font-sans transition-colors duration-500">
            <style>{customStyles}</style>
            <div className="absolute top-[-10%] left-[10%] w-[50%] h-[50%] rounded-full bg-emerald-600/10 blur-[120px] animate-blob pointer-events-none"></div>
            <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-12 rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-2xl max-w-lg w-full z-10 animate-in fade-in zoom-in-95">
                <div className="w-24 h-24 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-8">
                    <CheckCircle size={40} className="text-emerald-500 dark:text-emerald-400" strokeWidth={1.5} />
                </div>
                <h1 className="text-3xl font-semibold tracking-wide text-slate-900 dark:text-white mb-3">Interview Complete</h1>
                <p className="text-slate-500 dark:text-slate-400 mb-10 text-sm font-light leading-relaxed">Your responses have been successfully saved and submitted for review. You may now close this window.</p>
            </div>
        </div>
      );
  }

  if (!hasStarted) {
      return (
          <div className="min-h-[100dvh] bg-slate-50 dark:bg-[#05050A] flex flex-col items-center justify-center p-4 sm:p-6 text-center relative overflow-hidden font-sans transition-colors duration-500">
              <style>{customStyles}</style>
              <div className="absolute top-[10%] left-[20%] w-[40%] h-[40%] rounded-full bg-indigo-600/10 blur-[120px] animate-blob pointer-events-none"></div>
              <div className="absolute bottom-[10%] right-[20%] w-[40%] h-[40%] rounded-full bg-violet-600/10 blur-[120px] animate-blob pointer-events-none" style={{ animationDelay: '2s' }}></div>

              <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-50">
                  <button onClick={() => setIsDark(!isDark)} className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors duration-300 focus:outline-none ${isDark ? 'bg-indigo-500/20 border border-indigo-500/30' : 'bg-slate-200 border border-slate-300'}`}>
                    <span className={`inline-flex h-4 w-4 transform items-center justify-center rounded-full transition-transform duration-300 ${isDark ? 'translate-x-7 bg-indigo-400' : 'translate-x-1 bg-white shadow-sm'}`}>
                      {isDark ? <Moon size={10} className="text-[#0a0a0a]" /> : <Sun size={10} className="text-slate-400" />}
                    </span>
                  </button>
              </div>

              <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-6 sm:p-8 rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-2xl max-w-xl w-full flex flex-col items-center z-10 animate-in fade-in zoom-in-95 my-auto">
                  
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-100 dark:bg-white/5 rounded-full flex items-center justify-center mb-4 overflow-hidden border border-slate-200 dark:border-white/10 shadow-sm">
                      <img src={AI_AVATAR_URL} alt="AI" className="w-full h-full object-cover object-top" />
                  </div>
                  <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold tracking-wide text-slate-900 dark:text-white mb-2 leading-tight">{metadata.campaign_name}</h1>
                  
                  <div className="w-full bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20 rounded-2xl p-4 sm:p-5 mt-4 mb-6 text-left">
                      <h3 className="text-rose-600 dark:text-rose-400 text-[12px] font-bold tracking-wide mb-3 flex items-center gap-2"><AlertTriangle size={14}/> Interview Rules</h3>
                      <ul className="text-slate-700 dark:text-slate-300 text-xs font-light space-y-2 list-disc pl-4 leading-relaxed">
                          <li>You must share your entire screen. The interview will be recorded.</li>
                          <li>Tab switching and exiting full-screen is strictly monitored.</li>
                          <li>You will be <strong className="font-bold">automatically disqualified</strong> after {MAX_SWITCHES} tab switches.</li>
                      </ul>
                  </div>

                  <div className="w-full aspect-video bg-slate-900 dark:bg-black/60 rounded-2xl overflow-hidden mb-4 border border-slate-200 dark:border-white/10 relative shadow-inner">
                      <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover transform scale-x-[-1]"></video>
                      {!showVideo && <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-xs font-bold tracking-wide">Starting camera...</div>}
                  </div>

                  <div className="w-full bg-slate-50 dark:bg-black/40 rounded-2xl p-4 sm:p-5 mb-5 border border-slate-200 dark:border-white/5">
                      <div className="flex items-center justify-between mb-3 text-[12px] sm:text-xs font-bold tracking-wide">
                          <span className="flex items-center gap-2 text-slate-500 dark:text-slate-400"><Mic size={14}/> Mic Check</span>
                          <span className={micLevel > 5 ? 'text-emerald-500 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}>
                              {micLevel > 5 ? 'Audio detected' : 'Say something...'}
                          </span>
                      </div>
                      <div className="h-1.5 w-full bg-slate-200 dark:bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full bg-indigo-500 transition-all duration-75" style={{ width: `${micLevel}%` }}></div>
                      </div>
                  </div>

                  <button onClick={handleStartInterview} className="bg-indigo-600 hover:bg-indigo-500 text-white w-full py-3.5 sm:py-4 rounded-xl font-bold text-xs tracking-wide transition-all shadow-[0_0_15px_rgba(99,102,241,0.3)] mt-2">
                      Acknowledge & Join
                  </button>
              </div>
          </div>
      );
  }

  // --- LIVE INTERVIEW ROOM ---
  return (
    <div className="h-[100dvh] w-full bg-slate-50 dark:bg-[#05050A] text-slate-900 dark:text-slate-200 font-sans flex flex-col overflow-hidden relative transition-colors duration-500">
        <style>{customStyles}</style>
        <div className="absolute top-[-10%] left-[10%] w-[50%] h-[50%] rounded-full bg-indigo-600/5 blur-[120px] animate-blob pointer-events-none"></div>

        {tutorialStep > 0 && (
            <div className="fixed inset-0 z-[100] bg-slate-900/60 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300">
                <div className="bg-white dark:bg-[#111] border border-slate-200 dark:border-white/10 p-8 sm:p-10 rounded-[2rem] max-w-md w-full shadow-2xl relative">
                    
                    {tutorialStep === 1 && (
                        <div className="animate-in slide-in-from-bottom-4 fade-in">
                            <div className="w-16 h-16 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-full flex items-center justify-center mb-6 border border-indigo-100 dark:border-indigo-500/20 shadow-sm mx-auto">
                                <User size={28} strokeWidth={1.5} />
                            </div>
                            <h2 className="text-2xl font-semibold tracking-wide text-slate-900 dark:text-white text-center mb-4">Meet Your Interviewer</h2>
                            <p className="text-slate-500 dark:text-slate-400 text-sm font-light leading-relaxed text-center">
                                The AI agent will appear on the left. It will ask questions dynamically and evaluate your responses in real-time.
                            </p>
                        </div>
                    )}

                    {tutorialStep === 2 && (
                        <div className="animate-in slide-in-from-bottom-4 fade-in">
                            <div className="w-16 h-16 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-full flex items-center justify-center mb-6 border border-indigo-100 dark:border-indigo-500/20 shadow-sm mx-auto">
                                <Mic size={28} strokeWidth={1.5} />
                            </div>
                            <h2 className="text-2xl font-semibold tracking-wide text-slate-900 dark:text-white text-center mb-4">How to Respond</h2>
                            <p className="text-slate-500 dark:text-slate-400 text-sm font-light leading-relaxed text-center mb-4">
                                Click the <strong>Speak</strong> button below the camera to answer verbally. The AI will listen and transcribe your answer. 
                            </p>
                            <p className="text-slate-500 dark:text-slate-400 text-sm font-light leading-relaxed text-center">
                                Alternatively, you can use the chat box at the bottom of the screen to type your responses.
                            </p>
                        </div>
                    )}

                    {tutorialStep === 3 && (
                        <div className="animate-in slide-in-from-bottom-4 fade-in">
                            <div className="w-16 h-16 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-full flex items-center justify-center mb-6 border border-indigo-100 dark:border-indigo-500/20 shadow-sm mx-auto">
                                <Code size={28} strokeWidth={1.5} />
                            </div>
                            <h2 className="text-2xl font-semibold tracking-wide text-slate-900 dark:text-white text-center mb-4">Coding Tasks</h2>
                            <p className="text-slate-500 dark:text-slate-400 text-sm font-light leading-relaxed text-center">
                                If you are given a technical question, the right panel will automatically transform into a full code editor with a built-in compiler. You can run your code and explain your logic verbally.
                            </p>
                        </div>
                    )}

                    {tutorialStep === 4 && (
                        <div className="animate-in slide-in-from-bottom-4 fade-in">
                            <div className="w-16 h-16 bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 rounded-full flex items-center justify-center mb-6 border border-rose-100 dark:border-rose-500/20 shadow-sm mx-auto">
                                <PhoneOff size={28} strokeWidth={1.5} />
                            </div>
                            <h2 className="text-2xl font-semibold tracking-wide text-slate-900 dark:text-white text-center mb-4">Finishing Up</h2>
                            <p className="text-slate-500 dark:text-slate-400 text-sm font-light leading-relaxed text-center">
                                The AI will let you know when the interview is complete. If you need to end early, click the <strong>End</strong> button below your camera.
                            </p>
                        </div>
                    )}

                    <div className="flex items-center justify-between mt-10 pt-6 border-t border-slate-200 dark:border-white/10">
                        <div className="flex gap-1.5">
                            {[1, 2, 3, 4].map(dot => (
                                <div key={dot} className={`h-1.5 rounded-full transition-all duration-300 ${tutorialStep === dot ? 'w-4 bg-indigo-500' : 'w-1.5 bg-slate-300 dark:bg-white/20'}`}></div>
                            ))}
                        </div>
                        <div className="flex items-center gap-4">
                            {tutorialStep < 4 ? (
                                <>
                                    <button onClick={() => setTutorialStep(0)} className="text-[12px] font-bold tracking-wide text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors">Skip</button>
                                    <button onClick={() => setTutorialStep(prev => prev + 1)} className="px-5 py-2.5 bg-slate-100 dark:bg-white/10 hover:bg-slate-200 dark:hover:bg-white/20 text-slate-900 dark:text-white rounded-xl text-[12px] font-bold tracking-wide transition-colors">Next</button>
                                </>
                            ) : (
                                <button onClick={() => setTutorialStep(0)} className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-[12px] font-bold tracking-wide transition-colors shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)]">Start Interview</button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )}

        <header className="h-16 border-b border-slate-200 dark:border-white/10 flex items-center justify-between px-4 md:px-6 shrink-0 z-20 bg-white/80 dark:bg-[#0a0a0a]/50 backdrop-blur-md">
            <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden border border-slate-200 dark:border-white/10 bg-slate-100 dark:bg-white/5">
                    <img src={AI_AVATAR_URL} alt="AI" className="w-full h-full object-cover object-top" />
                </div>
                <div className="text-sm md:text-base font-light tracking-wide text-slate-900 dark:text-white truncate max-w-[200px] md:max-w-none">{metadata.campaign_name}</div>
            </div>
            
            <div className="flex items-center gap-4">
                <button onClick={() => setIsDark(!isDark)} className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors duration-300 focus:outline-none ${isDark ? 'bg-indigo-500/20 border border-indigo-500/30' : 'bg-slate-200 border border-slate-300'}`}>
                  <span className={`inline-flex h-4 w-4 transform items-center justify-center rounded-full transition-transform duration-300 ${isDark ? 'translate-x-7 bg-indigo-400' : 'translate-x-1 bg-white shadow-sm'}`}>
                    {isDark ? <Moon size={10} className="text-[#0a0a0a]" /> : <Sun size={10} className="text-slate-400" />}
                  </span>
                </button>
                
                {showEditor && (
                    <>
                        <button onClick={handleRunCode} className="flex items-center gap-2 px-4 md:px-5 py-2 bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 border border-transparent dark:border-white/10 text-slate-900 dark:text-white rounded-xl text-[12px] md:text-xs font-bold tracking-wide transition-colors shadow-sm">
                            <Play size={14} fill="currentColor" /> <span className="hidden md:inline">Run Code</span>
                        </button>
                        <button onClick={handleSubmitFinal} className="flex items-center gap-2 px-4 md:px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-[12px] md:text-xs font-bold tracking-wide transition-colors shadow-[0_0_10px_rgba(99,102,241,0.3)]">
                            <Check size={14} /> Submit
                        </button>
                    </>
                )}
                {!showEditor && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 rounded-lg text-[12px] font-bold tracking-wide border border-rose-200 dark:border-rose-500/20">
                        <div className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse"></div> Live
                    </div>
                )}
            </div>
        </header>

        {/* 🔥 FIX: Removed z-10 from main containers during tutorial to allow highlighted children to pop out over the overlay */}
        <div className={`flex-1 flex flex-col md:flex-row p-2 md:p-4 gap-2 md:gap-4 min-h-0 relative ${tutorialStep === 0 ? 'overflow-hidden z-10' : ''}`}>
            
            <div className={`flex flex-col gap-2 md:gap-4 shrink-0 transition-all duration-500 ease-in-out min-h-0 ${showEditor ? 'md:w-[240px]' : 'md:w-[320px] lg:w-[400px]'} ${tutorialStep === 0 ? 'z-10' : ''}`}>

                <div className="flex flex-row md:flex-col gap-2 md:gap-4 w-full shrink-0">
                    {/* 🔥 HIGHLIGHT FOR STEP 1: AI Agent */}
                    <div className={`flex-1 md:w-full aspect-[4/3] md:aspect-video rounded-2xl shadow-sm overflow-hidden bg-slate-200 dark:bg-black/50 border border-slate-300 dark:border-white/10 relative transition-all duration-300 ${isAiSpeaking ? 'ring-1 ring-indigo-500 shadow-[0_0_15px_rgba(99,102,241,0.3)]' : ''} ${tutorialStep === 1 ? 'ring-4 ring-indigo-500 shadow-[0_0_30px_rgba(99,102,241,0.5)] z-[110] scale-[1.02]' : ''}`}>
                        {/* 🔥 NEW: Simulated AI Avatar Speaking Animation using CSS transform toggled by interval */}
                        <img 
                            src={AI_AVATAR_URL} 
                            alt="AI Interviewer" 
                            className={`w-full h-full object-cover object-top transition-transform duration-150 ease-in-out opacity-80 ${isAiSpeaking && avatarFrame === 1 ? 'scale-105 brightness-110' : 'scale-100 brightness-100'}`} 
                        />
                        <div className={`absolute inset-0 bg-indigo-500/20 mix-blend-overlay transition-opacity duration-300 ${isAiSpeaking ? 'opacity-100 animate-pulse' : 'opacity-0'}`}></div>
                        <div className="absolute bottom-2 left-2 bg-white/80 dark:bg-black/60 backdrop-blur text-slate-900 dark:text-white text-[12px] px-2 py-1 rounded-md tracking-wide font-bold flex items-center gap-1.5 shadow-sm dark:shadow-none">
                            AI Agent {isAiSpeaking && <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400 animate-pulse"></div>}
                        </div>
                    </div>

                    <div className="flex-1 md:w-full aspect-[4/3] md:aspect-video rounded-2xl shadow-sm overflow-hidden bg-slate-200 dark:bg-black/50 border border-slate-300 dark:border-white/10 relative">
                        <video ref={videoRef} autoPlay muted playsInline className={`w-full h-full object-cover transform scale-x-[-1] ${!showVideo && 'opacity-0'}`}></video>
                        <div className="absolute bottom-2 left-2 bg-white/80 dark:bg-black/60 backdrop-blur text-slate-900 dark:text-white text-[12px] px-2 py-1 rounded-md tracking-wide font-bold flex items-center gap-1.5 shadow-sm dark:shadow-none">
                            You {isRecording && recordingSourceRef.current === 'chat' && <div className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse"></div>}
                        </div>
                    </div>
                </div>

                <div className={`bg-white/80 dark:bg-[#0a0a0a]/50 rounded-2xl p-2 flex flex-row md:flex-col gap-2 border border-slate-200 dark:border-white/10 shrink-0 ${tutorialStep === 0 ? 'backdrop-blur-md' : ''}`}>    
                    {/* 🔥 HIGHLIGHT FOR STEP 2: Speak Button */}
                    <div className={`relative transition-all duration-500 flex flex-col justify-center flex-1 md:flex-none group ${showEditor ? 'max-h-0 opacity-0 hidden md:block' : 'max-h-[60px] opacity-100'} ${tutorialStep === 2 ? 'ring-4 ring-indigo-500 shadow-[0_0_30px_rgba(99,102,241,0.5)] rounded-xl z-[110] scale-[1.02]' : ''}`}>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-[12px] font-bold tracking-wide rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 shadow-xl">
                            {isRecording ? "Mute Microphone" : "Unmute Microphone"}
                        </div>
                        
                        <button 
                            onClick={() => toggleRecording('chat')}
                            disabled={micCooldown > 0 || status !== 'connected' || tutorialStep > 0}
                            className={`w-full h-full md:py-3.5 rounded-xl font-bold text-[12px] md:text-xs tracking-wide flex items-center justify-center gap-2 transition-all ${
                                isRecording && recordingSourceRef.current === 'chat'
                                ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-500/20'
                                : 'bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 border border-transparent dark:border-white/10 hover:bg-slate-200 dark:hover:bg-white/10'
                            } disabled:opacity-50`}
                        >
                            {micCooldown > 0 ? <span>Wait {micCooldown}s</span> : isRecording && recordingSourceRef.current === 'chat' ? <><MicOff size={14}/> Stop</> : <><Mic size={14}/> Speak</>}
                        </button>
                    </div>
                    
                    <div className="flex gap-2 flex-1 md:flex-none">
                        {/* 🔥 HIGHLIGHT FOR STEP 4: End Button */}
                        <div className={`relative flex-1 group flex justify-center transition-all duration-300 ${tutorialStep === 4 ? 'ring-4 ring-rose-500 shadow-[0_0_30px_rgba(244,63,94,0.5)] rounded-xl z-[110] scale-[1.02]' : ''}`}>
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-[12px] font-bold tracking-wide rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 shadow-xl">
                                End Interview
                            </div>
                            <button 
                                onClick={handleEndInterview} 
                                disabled={metadata.is_custom || tutorialStep > 0}
                                className={`w-full py-3.5 md:py-2.5 rounded-xl text-[12px] tracking-wide font-bold flex items-center justify-center gap-1.5 transition-colors border ${
                                    metadata.is_custom 
                                    ? 'bg-slate-50 dark:bg-white/5 border-slate-100 dark:border-white/5 text-slate-400 dark:text-slate-600 cursor-not-allowed' 
                                    : 'bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/20 text-rose-600 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-500/20'
                                }`}
                            >
                                <PhoneOff size={14}/> <span className="hidden md:inline">{showEditor && "End"}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 flex flex-col bg-white/80 dark:bg-[#0a0a0a]/50 backdrop-blur-md rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-sm transition-all duration-500 ease-in-out min-h-0 overflow-hidden">
                
                {!showEditor ? (
                    <div className="w-full h-full flex flex-col min-h-0 relative">
                        <div className="w-full px-6 pt-5 pb-3 shrink-0 border-b border-slate-200 dark:border-white/5 flex items-center justify-between bg-slate-100 dark:bg-black/20">
                            <h3 className="font-medium tracking-wide text-slate-900 dark:text-white text-lg">Conversation</h3>
                            
                            <div className="flex items-center gap-2">
                                <span className="text-[12px] tracking-wide font-bold text-slate-500">Strikes:</span>
                                <div className="flex gap-1">
                                    {[1, 2, 3].map(dot => (
                                        <div key={dot} className={`w-2 h-2 rounded-full ${dot <= tabSwitches ? 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.6)]' : 'bg-slate-300 dark:bg-white/10'}`}></div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="w-full flex-1 overflow-y-auto px-4 py-4 min-h-0 flex flex-col custom-scrollbar">
                            <div className="w-full max-w-3xl mx-auto flex flex-col space-y-6">
                                {messages.length === 0 && <div className="text-center text-slate-400 dark:text-slate-500 text-xs font-bold tracking-wide mt-10">The interview will begin shortly...</div>}
                                
                                {messages.map((msg, idx) => (
                                    <div key={idx} className={`flex gap-3 md:gap-4 items-start ${msg.sender === 'user' ? 'flex-row-reverse' : ''}`}>
                                        <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-white/5 shrink-0 flex items-center justify-center overflow-hidden border border-slate-300 dark:border-white/10 shadow-sm">
                                            {msg.sender === 'ai' ? (
                                                <img src={AI_AVATAR_URL} alt="AI" className="w-full h-full object-cover object-top" />
                                            ) : (
                                                <User size={14} className="text-slate-500 dark:text-slate-400"/>
                                            )}
                                        </div>
                                        
                                        {/* UPDATED: Enhanced Readable Font for Transcript is preserved */}
                                        <div className={`p-4 rounded-2xl text-base font-light leading-relaxed shadow-sm max-w-[85%] ${
                                            msg.sender === 'ai' 
                                            ? 'bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-800 dark:text-slate-200 rounded-tl-none' 
                                            : 'bg-indigo-600 text-white rounded-tr-none'
                                        } ${!msg.isFinal ? 'opacity-70 animate-pulse' : ''}`}>
                                            {msg.text}
                                        </div>
                                    </div>
                                ))}
                                <div ref={messagesEndRef} />
                            </div>
                        </div>

                        <div className="w-full p-4 shrink-0 bg-slate-50 dark:bg-black/20 border-t border-slate-200 dark:border-white/5 flex justify-center">
                            <form onSubmit={handleSendMessage} className="w-full max-w-3xl flex bg-white dark:bg-black/40 rounded-2xl overflow-hidden border border-slate-300 dark:border-white/10 focus-within:border-indigo-500/50 focus-within:ring-1 focus-within:ring-indigo-500/50 transition-all shadow-sm dark:shadow-none">
                                <input 
                                    type="text" 
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder="Type your response..."
                                    disabled={tutorialStep > 0}
                                    className="w-full bg-transparent border-none outline-none text-sm placeholder:text-[12px] font-medium text-slate-900 dark:text-white px-5 py-4 placeholder-slate-400 dark:placeholder-slate-500 disabled:opacity-50"
                                />
                                <button type="submit" disabled={!input.trim() || tutorialStep > 0} className="px-6 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/20 transition-colors disabled:opacity-30 disabled:hover:bg-transparent">
                                    <Send size={18}/>
                                </button>
                            </form>
                        </div>
                    </div>
                ) : (
                    <div className="w-full h-full flex min-h-0 bg-white dark:bg-[#0D0D0D]">
                        <div className="w-[35%] flex flex-col min-h-0 border-r border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#111]">
                            <div className="flex-1 overflow-y-auto p-6 min-h-0 custom-scrollbar">
                                <h3 className="font-medium tracking-wide text-slate-900 dark:text-white text-xl mb-4">Task Description</h3>
                                <div className="text-sm text-slate-600 dark:text-slate-400 font-light leading-relaxed whitespace-pre-wrap">
                                    {problemData.question}
                                    
                                    {problemData.requirements?.length > 0 && (
                                        <div className="mt-8 mb-2">
                                            <h4 className="font-bold text-slate-500 dark:text-slate-500 mb-3 text-[12px] tracking-wide">Requirements</h4>
                                            <ul className="list-disc list-inside space-y-2 text-slate-700 dark:text-slate-400">
                                                {problemData.requirements.map((req, i) => <li key={i}>{req}</li>)}
                                            </ul>
                                        </div>
                                    )}

                                    {testCases.length > 0 && (
                                        <div className="mt-8 space-y-3">
                                            <h4 className="font-bold text-slate-500 dark:text-slate-500 mb-3 text-[12px] tracking-wide">Test Cases</h4>
                                            {testCases.map((tc, idx) => (
                                                <div key={idx} className="bg-white dark:bg-black/40 rounded-xl p-4 text-xs font-mono border border-slate-200 dark:border-white/5 shadow-sm">
                                                    <div className="mb-2"><span className="text-slate-500 font-bold mr-2 tracking-wide text-[12px]">Input:</span><span className="text-emerald-600 dark:text-emerald-400">{JSON.stringify(tc.input)}</span></div>
                                                    <div><span className="text-slate-500 font-bold mr-2 tracking-wide text-[12px]">Output:</span><span className="text-indigo-600 dark:text-indigo-400">{JSON.stringify(tc.output)}</span></div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="p-6 bg-white dark:bg-[#0a0a0a] border-t border-slate-200 dark:border-[#333] shrink-0">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-bold text-slate-500 dark:text-slate-400 text-[12px] tracking-wide">Logic Explanation</h3>
                                    
                                    <div className="relative group">
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 px-3 py-1.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-[12px] font-bold tracking-wide rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 shadow-xl hidden md:block">
                                            {isRecording ? "Mute Microphone" : "Unmute Microphone"}
                                        </div>
                                        
                                        <button 
                                            onClick={() => toggleRecording('editor')}
                                            disabled={micCooldown > 0 || tutorialStep > 0}
                                            className={`px-3 py-1.5 rounded-lg text-[12px] tracking-wide font-bold flex items-center gap-1.5 transition-colors border ${
                                                isRecording && recordingSourceRef.current === 'editor' 
                                                ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-500/20 animate-pulse' 
                                                : 'bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 border-transparent dark:border-white/10 hover:bg-slate-200 dark:hover:bg-white/10'
                                            }`}
                                        >
                                            {isRecording && recordingSourceRef.current === 'editor' ? <><MicOff size={12}/> Stop</> : <><Mic size={12}/> Record</>}
                                        </button>
                                    </div>
                                </div>
                                <div className="h-16 overflow-y-auto text-xs font-light text-slate-500 italic bg-slate-50 dark:bg-black/40 rounded-xl p-3 border border-slate-100 dark:border-white/5 custom-scrollbar">
                                    {explanationDraft || "Click record to speak your thought process..."}
                                </div>
                            </div>
                        </div>

                        <div className="flex-1 flex flex-col min-h-0 bg-slate-50 dark:bg-[#1E1E1E]">
                            <div className="px-4 py-3 border-b border-slate-200 dark:border-[#333] flex justify-between items-center bg-white dark:bg-[#1E1E1E] shrink-0">
                                <div className="flex gap-2">
                                    <div className="px-3 py-1 text-[12px] font-bold tracking-wide text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-black/40 border border-slate-200 dark:border-white/5 rounded-lg">main.{language === 'python' ? 'py' : language === 'java' ? 'java' : 'cpp'}</div>
                                </div>
                                <select 
                                    value={language}
                                    onChange={(e) => setLanguage(e.target.value)}
                                    className="text-[12px] bg-transparent text-slate-500 dark:text-slate-400 font-bold tracking-wide cursor-pointer outline-none hover:text-slate-900 dark:hover:text-white"
                                >
                                    <option value="python">Python</option>
                                    <option value="java">Java</option>
                                    <option value="c++">C++</option>
                                </select>
                            </div>
                            
                            <div className="flex-1 relative min-h-0">
                                <Editor
                                    height="100%"
                                    width="100%"
                                    theme={isDark ? "vs-dark" : "light"}
                                    language={language === 'c++' ? 'cpp' : language}
                                    value={userCode}
                                    onChange={(value) => setUserCode(value)}
                                    options={{ minimap: { enabled: false }, fontSize: 14, scrollBeyondLastLine: false, padding: { top: 16 } }}
                                />
                            </div>

                            <div className="h-[200px] border-t border-slate-300 dark:border-[#333] flex flex-col shrink-0 bg-slate-900 dark:bg-[#0D0D0D] text-slate-200">
                                <div className="px-4 py-3 bg-slate-800 dark:bg-[#111] flex items-center gap-2 border-b border-slate-700 dark:border-[#333] shrink-0">
                                    <Terminal size={14} className="text-slate-400 dark:text-slate-500"/>
                                    <span className="text-[12px] font-bold text-slate-300 dark:text-slate-400 tracking-wide">Terminal Output</span>
                                </div>
                                <div className="flex-1 p-4 overflow-y-auto font-mono text-xs font-light whitespace-pre-wrap min-h-0 text-emerald-400 custom-scrollbar">
                                    {consoleOutput}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
        </div>
    </div>
  );
};

export default Interview;